import { stripLegacyValidationProfiles } from './url-answer-validation';
import { dateTimeQuestionConfigSchema } from './date-rule-schema';
import type { DateRuleBounds } from './question-blocks';
import { z } from 'zod';
import { conversationSectionSchema, type ConversationSection } from './conversation-sections';
import type { StorageDestination, FileCollectionConstraints } from './integrations/storage/types';
import {
  MAX_ATTACHMENT_BYTES,
  SUPPORTED_MIME_TYPES,
  type InlineAttachment,
} from './file-attachment';
import type { DateTimeQuestionConfig, FieldType } from '../types';
import {
  MAX_QUESTION_HELP_TEXT_LENGTH,
  isSafeQuestionLinkUrl,
  normalizeSignatureAnswer,
  validateSignatureSubmission,
  type QuestionStructuredValue,
  type ReadableSignatureAnswerValue,
  type SignatureAnswerValue,
} from './question-blocks';
import {
  SIGNING_CONSENT_STATEMENT,
  SIGNING_CONSENT_VERSION,
  type RespondentSigningState,
  type SignatureConfirmation,
} from './signing-contract';

/** Allow one base64 attachment plus bounded text, metadata and JSON escaping overhead. */
export const MAX_CONVERSATION_REQUEST_BYTES = Math.ceil(MAX_ATTACHMENT_BYTES / 3) * 4 + 128 * 1024;

export { isEmailAnswer, isWebsiteAnswer } from './url-answer-validation';

/** Versioned definition settings, snapshotted once for each response. */
export interface ConversationExperienceConfig {
  version: 1;
  callsEnabled: boolean;
  /** Offer a call now, after an answer, or open call setup first. */
  callEntry?: 'offer' | 'after_first_answer' | 'call';
  storageDestination?: StorageDestination;
  sections?: ConversationSection[];
}

/**
 * Settings a respondent may see. Exactly these keys, so the strict client parse
 * accepts it and no authoring resource such as a file destination leaves the server.
 */
export interface RespondentConversationConfig {
  version: 1;
  callsEnabled: boolean;
  /** Public entry policy, independent of current call permission. */
  callEntry?: ConversationExperienceConfig['callEntry'];
}

/** Metric identity shared by authoring, immutable snapshots and controls. */
export interface ConversationRating {
  version: 1;
  preset: 'nps' | 'scale' | 'faces';
  lowLabel: string;
  highLabel: string;
  direction: 'ascending' | 'descending';
}

/** Explicit calendar controls carry only a lookup preference or a server-issued slot identity. */
export type CalendarControl =
  | { action: 'availability'; earliestDay?: string; timezone?: string }
  | { action: 'book'; slotId: string; attendeeEmail?: string };

/** Live availability exposed without calendar resources, credentials or attendee details. */
export interface RespondentCalendar {
  status: 'idle' | 'available' | 'empty';
  /** Next conversation input requested by the calendar handler, independent of reply wording. */
  input?: 'email' | 'reply';
  /** The respondent's selected live offer, retained while collecting their email. */
  selectedSlotId?: string;
  durationMinutes: number;
  timezone: string;
  slots: { id: string; startsAt: string; endsAt: string }[];
  /** A week of respondent-local dates bounded by the host's booking horizon. */
  page?: { startDay: string; endDay: string; firstDay: string; lastDay: string };
}

/** Public active interaction, with no resource credentials or model guidance. */
export interface RespondentField {
  fieldId: string;
  /** Stable transcript identity for this active authored question. */
  questionMessageId?: string;
  type: FieldType;
  label: string;
  /** Authored instruction intended for the person answering, not model guidance. */
  helpText?: string;
  /** Authored example shown inside the answer input. */
  placeholder?: string;
  required: boolean;
  answerChannel: 'any' | 'on_screen';
  options?: { id: string; label: string; image?: { imageUrl: string; alt: string } }[];
  min?: number;
  max?: number;
  step?: number;
  minLength?: number;
  maxLength?: number;
  /** Optional URL validation for text answers. */
  textFormat?: 'url';
  /** Stable Other selection requiring attached text. */
  otherOptionId?: string;
  minSelected?: number;
  maxSelected?: number;
  /** Explicit image-choice selection behavior. */
  selectionMode?: 'single' | 'multiple';
  /** Optional acknowledgement policy link. */
  checkbox?: { link?: { label: string; url: string } };
  /** Local date/time collection mode. */
  dateTime?: DateTimeQuestionConfig;
  /** Calendar-day limits resolved by the server from accepted earlier answers. */
  dateBounds?: DateRuleBounds;
  /** Public matrix axes used by the direct control. */
  matrix?: { rows: { id: string; label: string }[]; columns: { id: string; label: string }[] };
  /** Public address requirements used by the direct control. */
  address?: { requirePostalCode: boolean };
  /** Public click-map image, without storage metadata or model caption. */
  image?: { imageUrl: string; alt: string };
  minClicks?: number;
  maxClicks?: number;
  textPresentation?: 'short' | 'expanded';
  /** Presentation of ordinary choice controls; missing means automatic. */
  choicePresentation?: 'auto' | 'list' | 'dropdown';
  rating?: ConversationRating;
  calendar?: RespondentCalendar;
  fileConstraints?: FileCollectionConstraints;
  information?: {
    fieldId: string;
    title: string;
    body?: string;
    imageUrl?: string;
    imageStillUrl?: string;
    imageSource?: 'upload' | 'klipy';
    passive?: boolean;
    delivery?: 'spoken' | 'on_screen';
    canContinue?: boolean;
  };
}

/** Finalized thread message; unsent drafts never enter this contract. */
export interface RespondentMessage {
  id: string;
  role: 'user' | 'assistant';
  text: string;
  modality?: 'text' | 'control' | 'speech';
  fieldId?: string;
}

/** Safe projection of the current respondent's accepted field state. */
export interface RespondentAnswer {
  fieldId: string;
  value?: string | number | string[];
  displayValue?: string;
  /** Exact accepted choice IDs for restoring controls during correction. */
  optionIds?: string[];
  /** Free text attached to the selected Other option. */
  otherText?: string;
  /** Exact accepted structured value used to restore expanded controls. */
  structuredValue?: QuestionStructuredValue;
  status: 'answered' | 'skipped' | 'missing' | 'failed' | 'conflict';
  field?: RespondentField;
}

/** Authoritative, privacy-filtered state shared by main-area and in-call UI. */
export interface RespondentConversationState {
  version: 1;
  responseId: string;
  revision: number;
  status: 'collecting' | 'needs_attention' | 'ready' | 'submitted';
  entry: 'text' | 'call';
  canStartCall: boolean;
  canSubmit: boolean;
  activeField: RespondentField | null;
  progress: { resolved: number; total: number; answered: number; skipped: number };
  messages: RespondentMessage[];
  answers: RespondentAnswer[];
  /** Ineligible question identities retire previously displayed conditional chapters. */
  hiddenFieldIds?: string[];
  /** Explicit signing lifecycle for responses containing a completed signature. */
  signing?: RespondentSigningState;
}

/** Stable identity and optimistic concurrency precondition for every mutation. */
interface ConversationMutation {
  eventId: string;
  expectedRevision: number;
}

/** Browser intent. The server derives modality from the authenticated ingress. */
export type ConversationRequest =
  | { type: 'bootstrap' }
  | { type: 'calendar_availability'; fieldId: string; timezone: string; earliestDay?: string }
  | (ConversationMutation & { type: 'input'; fieldId: string; kind: 'text'; text: string; attachment?: InlineAttachment })
  | (ConversationMutation & { type: 'input'; fieldId: string; kind: 'files'; attachmentIds: string[] })
  | (ConversationMutation & { type: 'input'; fieldId: string; kind: 'answer'; value: string | number })
  | (ConversationMutation & { type: 'input'; fieldId: string; kind: 'control'; value?: number; optionIds?: string[]; otherText?: string; structuredValue?: QuestionStructuredValue; calendar?: CalendarControl })
  | (ConversationMutation & { type: 'presented' | 'skip' | 'continue_information'; fieldId: string })
  | (ConversationMutation & { type: 'submit'; scope: 'full'; signatureConfirmation?: SignatureConfirmation });

/** Safe failure codes shared across HTTP and the existing live socket. */
export type ConversationErrorCode = 'invalid_input' | 'stale_revision' | 'unavailable' | 'not_authorized' | 'not_ready' | 'input_not_allowed' | 'call_required' | 'signing_confirmation_required' | 'duplicate_email';

/** State acknowledgement only follows authoritative persistence. */
export type ConversationReply =
  | { ok: true; state: RespondentConversationState; acknowledgedEventId?: string }
  | { ok: false; code: ConversationErrorCode; state?: RespondentConversationState };

const identifier = z.string().min(1).max(200);
const revision = z.number().int().nonnegative();
const mutationShape = { eventId: identifier, expectedRevision: revision };
const attachmentSchema = z.object({
  mimeType: z.enum(SUPPORTED_MIME_TYPES),
  dataBase64: z.string().min(1).max(Math.ceil(MAX_ATTACHMENT_BYTES / 3) * 4),
  fileName: z.string().min(1).max(500),
  byteLength: z.number().int().positive().max(MAX_ATTACHMENT_BYTES),
}).strict();
const fileConstraintsSchema = z.object({ maxFiles: z.number().int().min(1).max(5), maxFileBytes: z.number().int().min(1).max(536870912), maxTotalBytes: z.number().int().min(1).max(1073741824), allowedMimeTypes: z.array(z.string().min(1).max(200)).max(100) }).strict();
/** Shared authoring settings for the builder, REST API and MCP creation tool. */
export const conversationConfigSchema = z.object({
  version: z.literal(1),
  callsEnabled: z.boolean(),
  callEntry: z.enum(['offer', 'after_first_answer', 'call']).optional(),
  sections: z.array(conversationSectionSchema).max(200).optional(),
  storageDestination: z.object({ provider: z.literal('google'), connectionId: identifier, folderId: identifier, folderName: z.string().min(1).max(255), folderPath: z.string().min(1).max(2000) }).strict().optional(),
}).strict();

const timezoneSchema = z.string().min(1).max(100).refine(value => {
  try { new Intl.DateTimeFormat('en', { timeZone: value }); return true; } catch { return false; }
});
const normalizedPointSchema = z.object({ x: z.number().min(0).max(1), y: z.number().min(0).max(1) }).strict();
const versionedTypedSignatureSchema = z.object({
  version: z.literal(2), kind: z.literal('typed'), typedName: z.string().trim().min(1).max(500),
}).strict();
const legacyTypedSignatureSchema = z.object({
  kind: z.literal('typed'), typedName: z.string().max(500),
}).strict();
const versionedDrawnSignatureShape = {
    version: z.literal(2),
    kind: z.literal('drawn'),
    encoding: z.literal('normalized-10000'),
    canvas: z.object({ width: z.number().int().min(1).max(10000), height: z.number().int().min(1).max(10000) }).strict(),
    strokes: z.array(z.array(z.tuple([
      z.number().int().min(0).max(10000),
      z.number().int().min(0).max(10000),
    ])).min(1)).min(1).max(64),
};
const versionedDrawnSignatureSchema = z.object({
  ...versionedDrawnSignatureShape,
  signerName: z.string().min(1).max(500),
}).strict()
  .refine(value => value.strokes.reduce((total, stroke) => total + stroke.length, 0) <= 4096)
  .refine(value => new TextEncoder().encode(JSON.stringify(value)).byteLength <= 65536);
const namelessVersionedDrawnSignatureSchema = z.object(versionedDrawnSignatureShape).strict()
  .refine(value => value.strokes.reduce((total, stroke) => total + stroke.length, 0) <= 4096)
  .refine(value => new TextEncoder().encode(JSON.stringify(value)).byteLength <= 65536);
const legacyDrawnSignatureSchema = z.object({ kind: z.literal('drawn'), strokes: z.array(z.object({
    points: z.array(normalizedPointSchema).min(1).max(4096),
  }).strict()).min(1).max(64) }).strict();
const writableSignatureSchema = z.union([
  versionedTypedSignatureSchema,
  versionedDrawnSignatureSchema,
  legacyTypedSignatureSchema,
]);
const signatureSchema = writableSignatureSchema.transform((value, context): SignatureAnswerValue => {
  const validated = validateSignatureSubmission(value);
  if (!validated.valid) {
    context.addIssue({ code: 'custom', message: 'Invalid signature value' });
    return z.NEVER;
  }
  return validated.value;
});
const readableSignatureSchema = z.union([
  writableSignatureSchema,
  namelessVersionedDrawnSignatureSchema,
  legacyDrawnSignatureSchema,
]).transform((value, context): ReadableSignatureAnswerValue => {
  const normalized = normalizeSignatureAnswer(value);
  if (!normalized) {
    context.addIssue({ code: 'custom', message: 'Invalid signature value' });
    return z.NEVER;
  }
  return normalized;
});
/*
 * Keep signature policy in the shared normalizer and submission validator. The
 * wire schema only bounds parser allocation before those helpers enforce names,
 * aspect ratio, compaction and the canonical byte ceiling.
 */
const nonSignatureStructuredValueSchema = z.union([
  z.boolean(),
  z.array(identifier).max(1000).refine(ids => new Set(ids).size === ids.length),
  z.object({ value: z.string().min(1).max(100), timezone: timezoneSchema.optional() }).strict(),
  z.object({ selections: z.record(identifier, z.union([identifier, z.array(identifier).min(1).max(200).refine(ids => new Set(ids).size === ids.length)])).refine(value => Object.keys(value).length <= 200) }).strict(),
  z.object({ line1: z.string().trim().min(1).max(10000), line2: z.string().trim().min(1).max(10000).optional(),
    city: z.string().trim().min(1).max(10000), region: z.string().trim().min(1).max(10000).optional(),
    postalCode: z.string().trim().min(1).max(10000).optional(), country: z.string().trim().min(1).max(200) }).strict(),
  z.object({ points: z.array(normalizedPointSchema).max(1000)
    .refine(points => new Set(points.map(point => `${point.x}:${point.y}`)).size === points.length) }).strict(),
]);
const structuredValueSchema = z.union([nonSignatureStructuredValueSchema, signatureSchema]);
const readableStructuredValueSchema = z.union([nonSignatureStructuredValueSchema, readableSignatureSchema]);
const calendarDaySchema = z.string().regex(/^\d{4}-\d{2}-\d{2}$/).refine(value => {
  const day = new Date(`${value}T00:00:00Z`);
  return Number.isFinite(day.getTime()) && day.toISOString().slice(0, 10) === value;
});
const calendarControlSchema = z.union([
  z.object({ action: z.literal('availability'), earliestDay: calendarDaySchema.optional(), timezone: timezoneSchema.optional() }).strict(),
  z.object({ action: z.literal('book'), slotId: identifier, attendeeEmail: z.email().max(254).optional() }).strict(),
]);
const calendarSchema = z.object({
  status: z.enum(['idle', 'available', 'empty']), durationMinutes: z.number().int().positive(), timezone: timezoneSchema,
  input: z.enum(['email', 'reply']).optional(),
  selectedSlotId: identifier.optional(),
  slots: z.array(z.object({ id: identifier, startsAt: z.iso.datetime({ offset: true }), endsAt: z.iso.datetime({ offset: true }) }).strict()
    .refine(slot => Date.parse(slot.endsAt) > Date.parse(slot.startsAt))).max(700),
  page: z.object({ startDay: calendarDaySchema, endDay: calendarDaySchema, firstDay: calendarDaySchema, lastDay: calendarDaySchema }).strict()
    .refine(page => page.firstDay <= page.startDay && page.startDay <= page.endDay && page.endDay <= page.lastDay).optional(),
}).strict();
const signingConfirmationSchema = z.object({
  version: z.literal(SIGNING_CONSENT_VERSION),
  accepted: z.literal(true),
  responseRevision: revision,
}).strict();

const requestSchema = z.union([
  z.object({ type: z.literal('bootstrap') }).strict(),
  z.object({ type: z.literal('calendar_availability'), fieldId: identifier, timezone: timezoneSchema, earliestDay: calendarDaySchema.optional() }).strict(),
  z.object({ ...mutationShape, type: z.literal('input'), fieldId: identifier,
    kind: z.literal('text'), text: z.string().max(10000), attachment: attachmentSchema.optional() }).strict()
    .refine(value => value.text.trim().length > 0 || value.attachment !== undefined),
  z.object({ ...mutationShape, type: z.literal('input'), fieldId: identifier,
    kind: z.literal('files'), attachmentIds: z.array(identifier).min(1).max(5).refine(ids => new Set(ids).size === ids.length) }).strict(),
  z.object({ ...mutationShape, type: z.literal('input'), fieldId: identifier,
    kind: z.literal('answer'), value: z.union([z.string().min(1).max(10000).refine(value => value.trim().length > 0), z.number()]),
  }).strict(),
  z.object({ ...mutationShape, type: z.literal('input'), fieldId: identifier,
    kind: z.literal('control'), value: z.number().optional(),
    optionIds: z.array(identifier).max(1000).optional(), otherText: z.string().min(1).max(10000).refine(value => value.trim().length > 0).optional(), structuredValue: structuredValueSchema.optional(), calendar: calendarControlSchema.optional(),
  }).strict().refine(value => [value.value, value.optionIds, value.structuredValue, value.calendar].filter(item => item !== undefined).length === 1
    && (value.otherText === undefined || value.optionIds !== undefined)
    && (!value.optionIds || new Set(value.optionIds).size === value.optionIds.length)),
  z.object({ ...mutationShape, type: z.enum(['presented', 'skip', 'continue_information']), fieldId: identifier }).strict(),
  z.object({ ...mutationShape, type: z.literal('submit'), scope: z.literal('full'),
    signatureConfirmation: signingConfirmationSchema.optional() }).strict()
    .refine(value => !value.signatureConfirmation || value.signatureConfirmation.responseRevision === value.expectedRevision),
]);

const ratingSchema = z.object({
  version: z.literal(1), preset: z.enum(['nps', 'scale', 'faces']),
  lowLabel: z.string().max(500), highLabel: z.string().max(500), direction: z.enum(['ascending', 'descending']),
}).strict();
const publicFieldSchema = z.preprocess(stripLegacyValidationProfiles, z.object({
  fieldId: identifier,
  questionMessageId: identifier.optional(),
  type: z.enum(['free_form_explore', 'short_text', 'number', 'email', 'phone', 'name', 'choice_single', 'choice_multi', 'information', 'calendar_booking', 'file_upload', 'checkbox', 'image_choice', 'ranking', 'date_time', 'matrix', 'address', 'signature', 'image_click_map']),
  label: z.string().max(10000), required: z.boolean(), answerChannel: z.enum(['any', 'on_screen']),
  helpText: z.string().max(MAX_QUESTION_HELP_TEXT_LENGTH).optional(), placeholder: z.string().max(600).optional(),
  options: z.array(z.object({ id: identifier, label: z.string().max(1000), image: z.object({ imageUrl: z.string().max(2048), alt: z.string().max(1000) }).strict().optional() }).strict()).max(1000).optional(),
  min: z.number().optional(), max: z.number().optional(), step: z.number().positive().optional(),
  minLength: revision.optional(), maxLength: revision.optional(),
  textFormat: z.literal('url').optional(),
  otherOptionId: identifier.optional(),
  minSelected: revision.optional(), maxSelected: revision.optional(),
  selectionMode: z.enum(['single', 'multiple']).optional(),
  checkbox: z.object({ link: z.object({ label: z.string().min(1).max(1000),
    url: z.string().min(1).max(2048).refine(isSafeQuestionLinkUrl) }).strict().optional() }).strict().optional(),
  dateTime: dateTimeQuestionConfigSchema.optional(),
  dateBounds: z.object({ min: z.string().length(10).optional(), max: z.string().length(10).optional(), unavailable: z.boolean() }).strict().optional(),
  matrix: z.object({ rows: z.array(z.object({ id: identifier, label: z.string().min(1).max(1000) }).strict()).min(1).max(200),
    columns: z.array(z.object({ id: identifier, label: z.string().min(1).max(1000) }).strict()).min(1).max(200) }).strict().optional(),
  address: z.object({ requirePostalCode: z.boolean() }).strict().optional(),
  image: z.object({ imageUrl: z.string().max(2048), alt: z.string().min(1).max(1000) }).strict().optional(),
  minClicks: z.number().int().min(1).max(1000).optional(), maxClicks: z.number().int().min(1).max(1000).optional(),
  choicePresentation: z.enum(['auto', 'list', 'dropdown']).optional(),
  textPresentation: z.enum(['short', 'expanded']).optional(), rating: ratingSchema.optional(), calendar: calendarSchema.optional(),
  fileConstraints: fileConstraintsSchema.optional(),
  information: z.object({ fieldId: identifier, title: z.string().max(1000), body: z.string().max(10000).optional(),
    imageUrl: z.string().max(2000).refine(value => value.startsWith('/api/workspace-assets/')
      || /^https:\/\/static(?:1|2)?\.klipy\.com\//i.test(value)).optional(),
    imageStillUrl: z.string().max(2000).refine(value => /^https:\/\/static(?:1|2)?\.klipy\.com\//i.test(value)).optional(),
    imageSource: z.enum(['upload', 'klipy']).optional(),
    passive: z.boolean().optional(), delivery: z.enum(['spoken', 'on_screen']).optional(), canContinue: z.boolean().optional(),
  }).strict().optional(),
}).strict());
const signingStateSchema = z.union([
  z.object({ status: z.literal('required'), version: z.literal(SIGNING_CONSENT_VERSION),
    statement: z.literal(SIGNING_CONSENT_STATEMENT), responseRevision: revision }).strict(),
  z.object({ status: z.literal('signed'), version: z.literal(SIGNING_CONSENT_VERSION) }).strict(),
]);
const stateSchema = z.object({
  version: z.literal(1), responseId: identifier, revision,
  status: z.enum(['collecting', 'needs_attention', 'ready', 'submitted']),
  entry: z.enum(['text', 'call']),
  canStartCall: z.boolean(), canSubmit: z.boolean(), activeField: publicFieldSchema.nullable(),
  progress: z.object({ resolved: revision, total: revision, answered: revision, skipped: revision }).strict()
    .refine(value => value.resolved <= value.total && value.answered + value.skipped === value.resolved),
  messages: z.array(z.object({ id: identifier, role: z.enum(['user', 'assistant']), text: z.string().max(1002000),
    modality: z.enum(['text', 'control', 'speech']).optional(), fieldId: identifier.optional(),
  }).strict()).max(2000),
  answers: z.array(z.object({ fieldId: identifier,
    value: z.union([z.string().max(100000), z.number(), z.array(z.string().max(1000)).max(1000)]).optional(),
    displayValue: z.string().max(1002000).optional(),
    otherText: z.string().min(1).max(10000).optional(),
    optionIds: z.array(identifier).max(1000).refine(ids => new Set(ids).size === ids.length).optional(),
    structuredValue: readableStructuredValueSchema.optional(),
    status: z.enum(['answered', 'skipped', 'missing', 'failed', 'conflict']),
    field: publicFieldSchema.optional(),
  }).strict().refine(value => !value.field || value.field.fieldId === value.fieldId)).max(200),
  hiddenFieldIds: z.array(identifier).max(200).refine(ids => new Set(ids).size === ids.length).optional(),
  signing: signingStateSchema.optional(),
}).strict().refine(value => !value.signing
  || (value.signing.status === 'required'
    ? value.status === 'ready' && value.signing.responseRevision === value.revision
    : value.status === 'submitted'));

/** Validate untrusted definition settings without inventing defaults. */
export function parseConversationConfig(value: unknown): ConversationExperienceConfig | null {
  const parsed = conversationConfigSchema.safeParse(value);
  return parsed.success ? parsed.data : null;
}

/**
 * Project validated settings for a public respondent payload. Calls stay off
 * unless the settings enable them and current permissions still allow them.
 *
 * @param config - Parsed session or response-snapshot settings.
 * @param callsAllowed - Whether the organization may currently take calls.
 */
export function toRespondentConversationConfig(
  config: ConversationExperienceConfig,
  callsAllowed: boolean,
): RespondentConversationConfig {
  return { version: config.version, callsEnabled: config.callsEnabled && callsAllowed,
    ...(config.callEntry ? { callEntry: config.callEntry } : {}) };
}

/** Validate a browser intent before it reaches the response authority. */
export function parseConversationRequest(value: unknown): ConversationRequest | null {
  const parsed = requestSchema.safeParse(value);
  return parsed.success ? parsed.data : null;
}

/** Validate the public server projection before rendering it. */
export function parseRespondentConversationState(value: unknown): RespondentConversationState | null {
  const parsed = stateSchema.safeParse(value);
  return parsed.success ? parsed.data : null;
}

/** Derive fresh entry from the snapshotted order, leaving established calls intact. */
export function deriveConversationEntry(
  fields: readonly { type: string; answerChannel?: string; responseMode?: string; delivery?: string }[],
  config: ConversationExperienceConfig,
  callAlreadyEstablished = false,
): 'text' | 'call' {
  if (callAlreadyEstablished) return 'call';
  if (!config.callsEnabled || config.callEntry !== 'call') return 'text';
  const first = fields[0];
  if (first?.type === 'information' && first.responseMode === 'passive') {
    return first.delivery === 'on_screen' ? 'text' : 'call';
  }
  return !first || first.answerChannel === 'on_screen' ? 'text' : 'call';
}
